import { Badge, Button, CircularProgress, CollapsibleSection, Icon } from '@civilgrid/ui/components';
import { getNonNullUserName } from '@civilgrid/ui/utils';
import { keyBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { editMember, resendInvite } from '~/api/organizations';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { capturePosthogEvent, captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { OrganizationDetail } from '~/common/types';
import Notify from '~/components2/Notify';
import rootStore from '~/store';
import { InviteTeamOrgDialog } from '../../common/Invite';
import { isRoleHigherThanMine } from './common';
import EditUserDetails from './EditUserDetails';
import orgMembersStore from './store';

export default function MembersComponent() {
  const { currentOrganizationDetail, me } = useSnapshot(rootStore) as typeof rootStore;
  const [supportMembersOpen, setSupportMembersOpen] = useState(false);

  const { members } = useSnapshot(orgMembersStore);

  useEffect(() => {
    if (!currentOrganizationDetail.isLoading) {
      orgMembersStore.loadMembers();
    }
  }, [currentOrganizationDetail]);

  function getRoleName(role: string) {
    if (currentOrganizationDetail.isLoading) {
      return role;
    } else {
      const roleMap = keyBy(currentOrganizationDetail?.roles ?? [], 'roleId');
      return roleMap[role]?.name ?? 'Error';
    }
  }

  function onSave() {
    orgMembersStore.loadMembers();
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const content = useMemo(() => {
    if (members === null) {
      return <CircularProgress indeterminate />;
    }

    const sectionStyle = 'px-[11px]';
    const sectionHeadingStyle = 'pl-1 text-base font-bold';
    const sectionDescriptionStyle = 'pl-1 mb-4 text-sm';
    const tableStyle = 'relative w-full table-auto';
    const tableScrollStyle = 'overflow-x-auto';
    const headingRowStyle = 'bg-white text-left text-sm font-bold';
    const rowStyle = 'hover:bg-secondary/5 group text-sm';
    const projectsCellStyle = 'p-1 pr-6 align-top';
    const nameCellStyle = 'whitespace-nowrap p-1 pr-4 align-top';
    const roleCellStyle = 'whitespace-nowrap p-1 pr-6 align-top';
    const emailCellStyle = 'whitespace-nowrap p-1 align-top';
    const nameColStyle = 'w-px min-w-36 whitespace-nowrap p-1 pr-4';
    const roleColStyle = 'w-px min-w-36 whitespace-nowrap p-1 pr-6';
    const projectsColStyle = 'w-px whitespace-nowrap p-1 pr-6';
    const emailColStyle = 'w-full p-1';
    const actionColStyle = 'sticky right-0 w-px bg-white';
    const actionCellStyle =
      'sticky right-0 bg-white p-1 align-top shadow-[-8px_0_8px_-8px_rgb(0_0_0/0.08)] group-hover:bg-[color-mix(in_srgb,var(--color-secondary)_5%,white)]';
    const actionRevealStyle = 'opacity-0 group-hover:opacity-100 focus-within:opacity-100';

    return (
      <div className="space-y-12">
        <div className={sectionStyle}>
          <div className={sectionHeadingStyle}>Organization Members</div>
          <div className={sectionDescriptionStyle}>Team members with access to all projects</div>
          <div className={tableScrollStyle}>
            <table className={tableStyle}>
              <thead>
                <tr className={headingRowStyle}>
                  <th className={nameColStyle}>Name</th>
                  <th className={roleColStyle}>Role</th>
                  <th className={emailColStyle}>Email</th>
                  <th className={actionColStyle} />
                </tr>
              </thead>
              <tbody>
                {members?.orgMembers.map((m, _i) => (
                  <tr key={m.user.id} className={rowStyle}>
                    <td className={nameCellStyle}>{m.user.name}</td>
                    <td className={roleCellStyle}>{getRoleName(m.role)}</td>
                    <td className={emailCellStyle}>{m.user.email}</td>
                    <td className={actionCellStyle}>
                      <div className={actionRevealStyle}>
                        <Can action={Action.UpdateOrgRoles}>
                          {!isRoleHigherThanMine(m.role) ? <EditUserDetails member={m} onSave={onSave} /> : null}
                        </Can>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {members?.guestMembers.length > 0 && (
          <div className={sectionStyle}>
            <div className={sectionHeadingStyle}>Guest Members</div>
            <div className={sectionDescriptionStyle}>Team members with access to a subset of projects</div>
            <div className={tableScrollStyle}>
              <table className={tableStyle}>
                <thead>
                  <tr className={headingRowStyle}>
                    <th className={nameColStyle}>Name</th>
                    <th className={roleColStyle}>Role</th>
                    <th className={projectsColStyle}>Projects</th>
                    <th className={emailColStyle}>Email</th>
                    <th className={actionColStyle} />
                  </tr>
                </thead>
                <tbody>
                  {members?.guestMembers.map((m) => (
                    <tr key={m.user.id} className={rowStyle}>
                      <td className={nameCellStyle}>{getNonNullUserName(m.user.name) || m.user.email}</td>
                      <td className={roleCellStyle}>{getRoleName(m.role)}</td>
                      <td className={projectsCellStyle}>
                        {m.projects.map((p, projectIndex) => (
                          <div
                            // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                            key={`${p.name}-project-${projectIndex}`}
                            className="whitespace-nowrap"
                          >
                            {p.name}
                          </div>
                        ))}
                      </td>
                      <td className={emailCellStyle}>{m.user.email}</td>
                      <td className={actionCellStyle}>
                        <div className={actionRevealStyle}>
                          <Can action={Action.UpdateOrgRoles}>
                            {!isRoleHigherThanMine(m.role) ? <EditUserDetails member={m} onSave={onSave} /> : null}
                          </Can>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {members?.pendingMembers.length > 0 && (
          <div className={sectionStyle}>
            <div className={sectionHeadingStyle}>Pending invites</div>
            <div className={sectionDescriptionStyle}></div>
            <div className={tableScrollStyle}>
              <table className={tableStyle}>
                <thead>
                  <tr className={headingRowStyle}>
                    <th className={nameColStyle}>Name</th>
                    <th className={roleColStyle}>Role</th>
                    <th className={emailColStyle}>Email</th>
                    <th className={actionColStyle} />
                  </tr>
                </thead>
                <tbody>
                  {members?.pendingMembers.map((m) => (
                    <tr key={m.user.id} className={rowStyle}>
                      <td className={nameCellStyle}>{getNonNullUserName(m.user.name) || m.user.email}</td>
                      <td className={roleCellStyle}>{getRoleName(m.role)}</td>
                      <td className={emailCellStyle}>{m.user.email}</td>
                      <td className={`${actionCellStyle} whitespace-nowrap text-right`}>
                        <div className={actionRevealStyle}>
                          <Button
                            variant="secondary"
                            data-testid={`org-members-resend-${m.user.id}`}
                            onClick={async () => {
                              await resendInvite((currentOrganizationDetail as OrganizationDetail).id, m.user.id);
                              Notify.info({
                                title: 'Invite Resent',
                                message: `Invite has been resent to ${m.user.email}`,
                              });
                              capturePosthogEvent(POSTHOG_EVENTS.organizationInviteResent, {
                                organization_id: (currentOrganizationDetail as OrganizationDetail).id,
                                member_user_id: m.user.id,
                              });
                            }}
                          >
                            Resend
                          </Button>
                          &nbsp;&nbsp;
                          <Button
                            variant="danger"
                            data-testid={`org-members-revoke-${m.user.id}`}
                            onClick={async () => {
                              await editMember((currentOrganizationDetail as OrganizationDetail).id, m.user.id, {
                                status: 'suspended',
                              });
                              orgMembersStore.loadMembers();
                              Notify.info({
                                title: 'Invite Revoked',
                                message: `${m.user.name}'s invite has been revoked`,
                              });
                              capturePosthogEvent(POSTHOG_EVENTS.organizationInviteRevoked, {
                                organization_id: (currentOrganizationDetail as OrganizationDetail).id,
                                member_user_id: m.user.id,
                              });
                            }}
                          >
                            Revoke
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!currentOrganizationDetail.isLoading && !currentOrganizationDetail.isInternal && (
          <div className="max-w-4xl overflow-hidden rounded-lg border border-grey-30 bg-white shadow-sm">
            <div className="flex flex-wrap items-center gap-4 p-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-blue-10 text-blue-60">
                <Icon icon="person" size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-grey-90">CivilGrid Support Access</div>
                <div className="text-sm text-grey-70">
                  Members of the CivilGrid support team can access your organization to help with setup and
                  troubleshooting.
                </div>
              </div>
              <Button variant="secondary" size="standard" asChild>
                <a
                  href="mailto:support@civilgrid.com"
                  data-testid="org-members-support-access-contact-link"
                  onClick={() =>
                    captureTypedPosthogEvent(POSTHOG_EVENTS.contactSupportClicked, {
                      organization_id: (currentOrganizationDetail as OrganizationDetail).id,
                      source: 'org_members_support_access_card',
                    })
                  }
                >
                  <Icon icon="chatBubble" size={14} />
                  Contact support
                </a>
              </Button>
            </div>
            {me?.isSuperAdmin && members.cgMembers.length > 0 && (
              <CollapsibleSection
                title="Support members"
                titleAdornment={<Badge kind="count">{members.cgMembers.length}</Badge>}
                actions={
                  <Badge kind="status" color="orange" size="small">
                    Staff only
                  </Badge>
                }
                open={supportMembersOpen}
                onOpenChange={setSupportMembersOpen}
                className="border-b-0 border-t border-grey-30"
                toggleTestId="org-members-support-members-toggle"
                data-testid="org-members-support-members-section"
              >
                <div className="px-2.5 pb-2">
                  <div className={tableScrollStyle}>
                    <table className={tableStyle}>
                      <thead>
                        <tr className={headingRowStyle}>
                          <th className={nameColStyle}>Name</th>
                          <th className={roleColStyle}>Role</th>
                          <th className={emailColStyle}>Email</th>
                          <th className={actionColStyle} />
                        </tr>
                      </thead>
                      <tbody>
                        {members.cgMembers.map((m) => (
                          <tr key={m.user.id} className={rowStyle}>
                            <td className={nameCellStyle}>{getNonNullUserName(m.user.name) || m.user.email}</td>
                            <td className={roleCellStyle}>{getRoleName(m.role)}</td>
                            <td className={emailCellStyle}>{m.user.email}</td>
                            <td className={actionCellStyle}>
                              <div className={actionRevealStyle}>
                                <Can action={Action.UpdateOrgRoles}>
                                  {!isRoleHigherThanMine(m.role) ? (
                                    <EditUserDetails member={m} onSave={onSave} />
                                  ) : null}
                                </Can>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CollapsibleSection>
            )}
          </div>
        )}
      </div>
    );
  }, [members, me, supportMembersOpen, currentOrganizationDetail]);

  return (
    <div className="p-2 pb-48">
      <div className="mb-4 px-[11px] w-fit">
        <Can action={Action.InviteOrgMembers}>
          <InviteTeamOrgDialog
            trigger={<Button data-testid="org-members-invite-button">Invite Team</Button>}
            onSave={onSave}
          />
        </Can>
      </div>
      <div>{content}</div>
    </div>
  );
}
